import API from './API.ts'

export default {
  //
  // GET
  //
  getBookings () {
    return API.httpClient.get('bookings/synced')
  },
  reportActionItemsSyncDiagnostics (payload) {
    return API.httpClient.post('bookings/action-items-sync-diagnostics', payload)
  },
  getBookingDialogContent ({ bookingId }) {
    return API.httpClient.get('bookings/dialog-content', {
      params: {
        booking_id: bookingId
      }
    })
  },
  getBooking ({ bookingId }) {
    return API.httpClient.get('bookings/show', {
      params: {
        booking_id: bookingId
      }
    })
  },
  getBookingCart (bookingId, readConsumption = false) {
    return API.httpClient.get(readConsumption ? 'bookings/consumption' : 'bookings/cart', {
      params: {
        booking_id: bookingId,
        defer_consumption: true
      }
    })
  },
  getBookingCharges (bookingId) {
    return API.httpClient.get('bookings/charges', {
      params: {
        booking_id: bookingId
      }
    })
  },
  getBookingInvoices (bookingId) {
    return API.httpClient.get('bookings/invoices', {
      params: {
        booking_id: bookingId
      }
    })
  },
  getBookingReceipts (bookingId) {
    return API.httpClient.get('bookings/receipts', {
      params: {
        booking_id: bookingId
      }
    })
  },
  getBookingPayments (bookingId) {
    return API.httpClient.get('bookings/payments', {
      params: {
        booking_id: bookingId
      }
    })
  },
  getBookingEmails (bookingId) {
    return API.httpClient.get('bookings/emails', {
      params: {
        booking_id: bookingId
      }
    })
  },
  getEditingRecords (bookingId) {
    return API.httpClient.get('bookings/editing-records', {
      params: {
        booking_id: bookingId
      }
    })
  },
  getAccommodationSums (bookingId) {
    return API.httpClient.get('bookings/accommodation-sums', {
      params: {
        booking_id: bookingId
      }
    })
  },
  getAccommodationChargesCalendarItems (bookingId) {
    return API.httpClient.get('bookings/accommodation-charges-calendar', {
      params: {
        booking_id: bookingId
      }
    })
  },
  getBookingsIndex ({ requestId, offset, visibleBookings, sortedColumn, sortedDesc, filterCustom, checkinDateFilterDate, checkinDateFilterRange, checkoutDateFilterDate, checkoutDateFilterRange, bookingDateFilterDate, bookingDateFilterRange, searchString }) {
    return API.httpClient.get('bookings', {
      params: {
        request_id: requestId,
        offset,
        visible_bookings: visibleBookings,
        sorted_column: sortedColumn,
        sorted_desc: sortedDesc,
        filter_custom: filterCustom ?? [],
        checkin_date_filter_date: checkinDateFilterDate?.format('YYYY-MM-DD') ?? null,
        checkin_date_filter_range: (checkinDateFilterRange[0] && checkinDateFilterRange[1])
          ? [
              checkinDateFilterRange[0]?.format('YYYY-MM-DD') ?? null,
              checkinDateFilterRange[1]?.format('YYYY-MM-DD') ?? null
            ]
          : [],
        checkout_date_filter_date: checkoutDateFilterDate?.format('YYYY-MM-DD') ?? null,
        checkout_date_filter_range: (checkoutDateFilterRange[0] && checkoutDateFilterRange[1])
          ? [
              checkoutDateFilterRange[0]?.format('YYYY-MM-DD') ?? null,
              checkoutDateFilterRange[1]?.format('YYYY-MM-DD') ?? null
            ]
          : [],
        booking_date_filter_date: bookingDateFilterDate?.format('YYYY-MM-DD') ?? null,
        booking_date_filter_range: (bookingDateFilterRange[0] && bookingDateFilterRange[1])
          ? [
              bookingDateFilterRange[0]?.format('YYYY-MM-DD') ?? null,
              bookingDateFilterRange[1]?.format('YYYY-MM-DD') ?? null
            ]
          : [],
        search_string: searchString
      }
    })
  },
  getBookingSummary (bookingId) {
    return API.httpClient.get('calendar/booking-summary', {
      params: {
        booking_id: bookingId
      }
    })
  },
  getBookingsPaymentStatuses () {
    return API.httpClient.get('bookings/payment-statuses')
  },
  getAvailableCheckinDates ({ bookingId }) {
    return API.httpClient.get('bookings/available-checkin-dates', {
      params: {
        booking_id: bookingId
      }
    })
  },
  getAvailableCheckoutDates ({ bookingId, selectedCheckinDate }) {
    return API.httpClient.get('bookings/available-checkout-dates', {
      params: {
        booking_id: bookingId,
        selected_checkin_date: selectedCheckinDate.format('YYYY-MM-DD')
      }
    })
  },

  //
  // POST
  //
  printCheckinConfirmation ({ bookingId }) {
    return API.httpClient.post('bookings/print-checkin-confirmation', {
      booking_id: bookingId
    })
  },
  deleteDraft (bookingId) {
    return API.httpClient.post('bookings/delete-draft', {
      booking_id: bookingId
    })
  },
  parkBooking ({ bookingId, charges }) {
    const items = charges.filter(charge => charge.price !== null && charge.quantity !== null).map((charge) => {
      const item = Object.assign({}, charge)
      item.delivery_date = charge.delivery_date ? charge.delivery_date.format('YYYY-MM-DD') : null
      item.position_id = charge.position ? charge.position.id : null
      item.product_id = charge.product.id
      item.position = null
      item.product = null
      return item
    })
    return API.httpClient.post('bookings/park', {
      booking_id: bookingId,
      items
    })
  },
  saveBooking ({ booking, total, isApplyBookingFee, isApplyPositionReservationFee, isApplyElectricityFee }) {
    const customer = Object.assign({}, booking.customer)
    customer.bookings = null
    const payload = {
      customer_id: booking.customer.id,
      customer,
      position_id: booking.position?.id,
      category_id: booking.category.id,
      check_in: booking.checkIn.format('YYYY-MM-DD'),
      check_out: booking.checkOut.format('YYYY-MM-DD'),
      adults: booking.adults,
      children: booking.children,
      dogs: booking.dogs,
      expected_arrival_time: booking.expected_arrival_time,
      booking_field_changes: booking.booking_field_changes || [],
      lodging_type_id: booking.lodgingType?.id,
      cancellation_policy_id: booking.cancellationPolicy?.id,
      type: booking.type,
      season_id: booking.season?.id,
      accommodation_price: booking.accommodation_price,
      total,
      is_apply_booking_fee: isApplyBookingFee,
      is_apply_position_reservation_fee: isApplyPositionReservationFee,
      is_apply_electricity_fee: isApplyElectricityFee
    }

    // Skicka med CKE-kortnummer om det finns kopplat via SCR-scanning
    if (booking.cke_card_number) {
      payload.cke_card_number = booking.cke_card_number
    }

    return API.httpClient.post('bookings/store', payload)
  },
  updateBooking ({ booking, rebookingOption, isApplyBookingFee, isApplyPositionReservationFee, isApplyElectricityFee }) {
    const customer = Object.assign({}, booking.customer)
    customer.bookings = null
    return API.httpClient.post('bookings/update', {
      booking_id: booking.id,
      customer_id: booking.customer.id,
      customer,
      position_id: booking.position?.id,
      category_id: booking.category.id,
      check_in: booking.checkIn.format('YYYY-MM-DD'),
      check_out: booking.checkOut.format('YYYY-MM-DD'),
      adults: booking.adults,
      children: booking.children,
      dogs: booking.dogs,
      expected_arrival_time: booking.expected_arrival_time,
      booking_field_changes: booking.booking_field_changes || [],
      lodging_type_id: booking.lodgingType?.id,
      cancellation_policy_id: booking.cancellationPolicy?.id,
      type: booking.type,
      season_id: booking.season?.id,
      accommodation_price: booking.accommodation_price,
      rebooking_option: rebookingOption,
      is_apply_booking_fee: isApplyBookingFee,
      is_apply_position_reservation_fee: isApplyPositionReservationFee,
      is_apply_electricity_fee: isApplyElectricityFee
    })
  },
  updateRemarks ({ bookingId, remarks }) {
    return API.httpClient.post('bookings/updateremarks', {
      booking_id: bookingId,
      remarks,
      timestamp: Date.now()
    })
  },
  sendBookingConfirmation ({ bookingId }) {
    return API.httpClient.post('bookings/send-confirmation', {
      booking_id: bookingId
    })
  },
  sendCheckinEmail ({ bookingId }) {
    return API.httpClient.post('bookings/send-checkin-email', {
      booking_id: bookingId
    })
  },
  sendCheckinConfirmation ({ bookingId }) {
    return API.httpClient.post('bookings/send-checkin-confirmation', {
      booking_id: bookingId
    })
  },
  checkIn ({ bookingId, checkinDate, skipCleaningCheck }) {
    return API.httpClient.post('bookings/check-in', {
      booking_id: bookingId,
      check_in_date: checkinDate.format('YYYY-MM-DD'),
      skip_cleaning_check: skipCleaningCheck
    })
  },
  reverseCheckin (bookingId) {
    return API.httpClient.post('bookings/reverse-checkin', {
      booking_id: bookingId
    })
  },
  checkOut (bookingId) {
    return API.httpClient.post('bookings/check-out', {
      booking_id: bookingId
    })
  },
  updateCheckoutDate ({ bookingId, checkoutDate }) {
    return API.httpClient.post('bookings/update-checkout', {
      booking_id: bookingId,
      checkout_date: checkoutDate.format('YYYY-MM-DD')
    })
  },
  reverseCheckout (bookingId) {
    return API.httpClient.post('bookings/reverse-checkout', {
      booking_id: bookingId
    })
  },
  checkinAndCheckout ({ bookingId, checkinDate, checkoutDate }) {
    return API.httpClient.post('bookings/checkin-and-checkout', {
      booking_id: bookingId,
      checkin_date: checkinDate.format('YYYY-MM-DD'),
      checkout_date: checkoutDate.format('YYYY-MM-DD')
    })
  },
  cancel (bookingId) {
    return API.httpClient.post('bookings/cancel', {
      booking_id: bookingId
    })
  },
  reverseCancel (bookingId) {
    return API.httpClient.post('booking-cancellations/reverse-cancellation', {
      booking_id: bookingId
    })
  },
  saveAccommodationCharges ({ bookingId, dates, isPeriodAccommodationCharge, price, discountType, discountValue, discountName, originalPrice }) {
    // För nya accommodation charges, skapade av användaren
    return API.httpClient.post('bookings/update-accommodation-charges', {
      booking_id: bookingId,
      dates,
      is_period_accommodation_charge: isPeriodAccommodationCharge,
      price,
      discount_name: discountName,
      discount_type: discountType,
      discount_value: discountValue,
      original_price: originalPrice
    })
  },
  setAccommodationPrice ({ bookingId, price }) {
    return API.httpClient.post('bookings/set-accommodation-price', {
      booking_id: bookingId,
      price
    })
  },
  updateDeliveryDate ({ chargeId, salesItemId, deliveryDate }) {
    return API.httpClient.post('sales/update-delivery-date', {
      delivery_date: deliveryDate ? deliveryDate.format('YYYY-MM-DD') : null,
      charge_id: chargeId,
      sales_item_id: salesItemId
    })
  }
}
